import { Body, Circle } from "p2";

import { collisionGroups } from "../../config/CollisionGroups";
import { Colors } from "../../config/DesignConstants";
import { V, V2d } from "../../core/Vector";
import VectorSprite from "../graphics/VectorSprite";
import {
  BaseMunition,
  ExplosionConfig,
  MunitionMovement,
  MunitionType,
  MunitionVisualConfig,
} from "./BaseMunition";
import { GravityMovement } from "./movements/GravityMovement";

// Bomb physics constants
const BOMB_RADIUS = 4;
const BOMB_MASS = 0.5;
const INITIAL_VELOCITY_MULTIPLIER = 2;

// Bomb rendering constants
const BOMB_POLYGON_POINTS = 8;
const BOMB_STROKE_WIDTH = 1;
const BOMB_ROTATION_SPEED = 0.05;

// Bomb explosion constants
const BOMB_EXPLOSION_RADIUS = 25;
const BOMB_EXPLOSION_LIFETIME = 1.2;
const BOMB_DAMAGE = 10;

export class BasicBomb extends BaseMunition {
  munitionType = MunitionType.BOMB;
  damage = BOMB_DAMAGE;

  private renderer!: VectorSprite;
  private gravityMovement!: GravityMovement;

  constructor(position: V2d) {
    super(position);
    this.tags = ["bomb", "levelEntity"];
  }

  protected createBody(position: V2d): Body {
    const body = new Body({ mass: BOMB_MASS });
    const shape = new Circle({ radius: BOMB_RADIUS });
    shape.collisionGroup = collisionGroups("Bomb");
    shape.collisionMask = collisionGroups("Turret", "Bullet", "Explosion");
    body.addShape(shape);
    body.position = [position.x, position.y];

    // Add initial velocity toward center
    const directionToCenter = V(0, 0)
      .sub(position)
      .normalize()
      .imul(INITIAL_VELOCITY_MULTIPLIER);
    body.velocity = [directionToCenter.x, directionToCenter.y];

    return body;
  }

  protected setupVisual(): void {
    const visualConfig = this.getVisualConfig();
    this.renderer = VectorSprite.fromPolygon(
      visualConfig.points,
      visualConfig.strokeColor,
      visualConfig.strokeWidth || 1,
    );
    this.addChild(this.renderer);
  }

  getMovementBehavior(): MunitionMovement {
    this.gravityMovement = new GravityMovement();
    return this.gravityMovement;
  }

  getVisualConfig(): MunitionVisualConfig {
    // Create a spiky star shape by alternating inner and outer radii
    const points: V2d[] = [];
    const outerRadius = BOMB_RADIUS;
    const innerRadius = BOMB_RADIUS * 0.5;
    const spikes = BOMB_POLYGON_POINTS;

    for (let i = 0; i < spikes * 2; i++) {
      const angle = (i * Math.PI) / spikes;
      const radius = i % 2 === 0 ? outerRadius : innerRadius;
      points.push(V(Math.cos(angle) * radius, Math.sin(angle) * radius));
    }

    return {
      points,
      strokeColor: Colors.RED,
      strokeWidth: BOMB_STROKE_WIDTH,
    };
  }

  getExplosionConfig(): ExplosionConfig {
    return {
      radius: BOMB_EXPLOSION_RADIUS,
      lifetime: BOMB_EXPLOSION_LIFETIME,
      color: Colors.PINK,
      damage: this.damage,
    };
  }

  protected updateVisualPosition(): void {
    if (this.body && this.renderer) {
      this.renderer.setPosition(this.body.position);
      // Add spinning rotation
      this.renderer.setRotation(
        this.renderer.getRotation() + BOMB_ROTATION_SPEED,
      );
    }
  }

  protected getExplosionType(): "bomb" {
    return "bomb";
  }

  /**
   * Get the renderer for applying visual effects
   * @example bomb.getRenderer().addFilter(new FlashFilter(...))
   */
  getRenderer(): VectorSprite {
    return this.renderer;
  }
}
